import React, { useEffect, useState } from "react";
import { api, ApiError, resolveUrl, type Property, type LedgerEntry } from "../../api";
import { useAuth } from "../../AuthContext";
import { ClaimsToConfirm, LEDGER_CHANGED_EVENT } from "./ClaimsToConfirm";
import { useToast } from "../../ToastContext";
import { EndTenancyForm } from "./EndTenancyForm";
import { RentalAgreement } from "../shared/RentalAgreement";
import { HomePromoCarousel } from "../shared/HomePromoCarousel";
import { BoostListing } from "./BoostListing";
import { useLanguage } from "../../i18n";
import { initials } from "../../format";

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

function TenantFollowUpRow({
  row,
  onMarkPaid,
  onRemind,
  onOpen,
}: {
  row: PendingTenant;
  onMarkPaid: (entryId: string) => Promise<void>;
  onRemind: (entryId: string) => Promise<void>;
  onOpen: () => void;
}) {
  const { t } = useLanguage();
  const [marking, setMarking] = useState(false);
  const [reminding, setReminding] = useState(false);

  async function markPaid(e: React.MouseEvent) {
    e.stopPropagation();
    setMarking(true);
    try {
      await onMarkPaid(row.entry.id);
    } finally {
      setMarking(false);
    }
  }

  async function remind(e: React.MouseEvent) {
    e.stopPropagation();
    setReminding(true);
    try {
      await onRemind(row.entry.id);
    } finally {
      setReminding(false);
    }
  }

  const subLabel = [row.propertyLabel, row.unitLabel ?? row.bedLabel ?? null].filter(Boolean).join(" · ");

  return (
    <div className="tenancy-row-card pending" onClick={onOpen}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div className="tenancy-avatar pending">{initials(row.tenantName)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="tenancy-name">{row.tenantName}</div>
          <div className="tenancy-sub">{subLabel}</div>
        </div>
        <span className="tenancy-amt pending">{fmtINR(row.entry.amount)}</span>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button className="btn-outline btn-sm" style={{ flex: 1 }} disabled={marking} onClick={markPaid}>
          {marking ? t("ownerLedgers.markingBusy") : t("ownerLedgers.markReceived")}
        </button>
        <button className="btn-outline btn-sm" style={{ flex: 1 }} disabled={reminding} onClick={remind}>
          {reminding ? t("ownerLedgers.sendingBusy") : t("ownerLedgers.remind")}
        </button>
      </div>
    </div>
  );
}

interface PendingTenant {
  tenancyId: string;
  tenantName: string;
  unitLabel: string | null;
  bedLabel: string | null;
  propertyLabel: string;
  entry: LedgerEntry;
}

function ProgressRing({
  pct,
  size,
  stroke,
  track,
  color,
  children,
}: {
  pct: number;
  size: number;
  stroke: number;
  track: string;
  color: string;
  children: React.ReactNode;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={`${dash} ${c}`}
          strokeLinecap="round"
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {children}
      </div>
    </div>
  );
}

const HOUSE_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    <path d="M9 22V12h6v10" />
  </svg>
);
const PG_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20h20M4 20V10l8-6 8 6v10M9 20v-6h6v6" />
  </svg>
);
const COMMERCIAL_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M4 21V8l8-5 8 5v13M9 21v-5h6v5M9 12h.01M15 12h.01M9 8h.01M15 8h.01" />
  </svg>
);
const COMMERCIAL_TYPES = ["Shop", "Office", "Showroom", "Warehouse", "Co-working"];

function PropThumb({ p }: { p: Property }) {
  const icon = p.category === "pg" ? PG_ICON : COMMERCIAL_TYPES.includes(p.type ?? "") ? COMMERCIAL_ICON : HOUSE_ICON;
  return <div className="prop-thumb">{p.photos[0] ? <img src={resolveUrl(p.photos[0])} alt="" /> : icon}</div>;
}

function useCategoryLabel() {
  const { t } = useLanguage();
  return (p: Property) =>
    p.category === "pg" ? t("ownerHome.filterPg") : p.category === "commercial" ? t("ownerHome.filterCommercial") : t("ownerHome.filterResidential");
}

function CategoryChip({ p }: { p: Property }) {
  const categoryLabel = useCategoryLabel();
  return <span className="prop-chip category">{categoryLabel(p)}</span>;
}

export function OwnerHome({ onNavigate }: { onNavigate: (v: "listings" | "ledgers" | "requests" | "refer", propertyId?: string) => void }) {
  const [properties, setProperties] = useState<Property[] | null>(null);
  const [ledgerStatus, setLedgerStatus] = useState<Record<string, "paid" | "pending">>({});
  const [received, setReceived] = useState(0);
  const [pending, setPending] = useState(0);
  const [pendingEntryIds, setPendingEntryIds] = useState<string[]>([]);
  const [pendingTenants, setPendingTenants] = useState<PendingTenant[]>([]);
  const [remindingAll, setRemindingAll] = useState(false);
  const [filter, setFilter] = useState<"all" | "residential" | "commercial" | "pg">("all");
  const [showTenants, setShowTenants] = useState(false);
  const toast = useToast();
  const { t } = useLanguage();

  async function load() {
    const { properties } = await api.get("/api/properties/mine");
    setProperties(properties);

    let rec = 0,
      pend = 0;
    const statusMap: Record<string, "paid" | "pending"> = {};
    const pendingIds: string[] = [];
    const pendingRows: PendingTenant[] = [];
    for (const p of properties as Property[]) {
      const { ledgers } = await api.get(`/api/ledger/property/${p.id}`);
      for (const l of ledgers) {
        const latest = l.entries[0];
        if (!latest) continue;
        if (latest.status === "paid") rec += latest.amount;
        else {
          pend += latest.amount;
          pendingIds.push(latest.id);
          pendingRows.push({
            tenancyId: l.tenancyId,
            tenantName: l.tenantName,
            unitLabel: l.unitLabel,
            bedLabel: l.bedLabel,
            propertyLabel: p.label,
            entry: latest,
          });
        }
        if (p.category !== "pg" && !p.units?.length) statusMap[p.id] = latest.status;
      }
    }
    setReceived(rec);
    setPending(pend);
    setPendingEntryIds(pendingIds);
    setPendingTenants(pendingRows);
    setLedgerStatus(statusMap);
  }

  async function markTenantPaid(entryId: string) {
    await api.patch(`/api/ledger/${entryId}`, { status: "paid", mode: "cash" });
    toast(t("ownerHome.paymentMarkedToast"));
    load();
  }

  async function remindTenant(entryId: string) {
    await api.post(`/api/ledger/${entryId}/remind`, {});
    toast(t("ownerHome.reminderSentToast"));
    load();
  }

  async function remindAll() {
    setRemindingAll(true);
    let sent = 0;
    for (const id of pendingEntryIds) {
      try {
        await api.post(`/api/ledger/${id}/remind`, {});
        sent++;
      } catch {
        // one tenant's reminder failing shouldn't stop the rest
      }
    }
    setRemindingAll(false);
    toast(sent > 0 ? t("ownerHome.remindersSent", { count: sent }) : t("ownerHome.couldNotSendReminders"));
    load();
  }

  useEffect(() => {
    load();
    // A payment confirmed or rejected from the dashboard card changes the
    // Received/Pending numbers above it, so reload when that happens.
    window.addEventListener(LEDGER_CHANGED_EVENT, load);
    return () => window.removeEventListener(LEDGER_CHANGED_EVENT, load);
  }, []);

  async function toggleToLet(p: Property) {
    try {
      const { property } = await api.patch(`/api/properties/${p.id}`, { toLet: !p.toLet });
      setProperties((props) => (props ? props.map((x) => (x.id === p.id ? property : x)) : props));
      toast(property.toLet ? t("ownerHome.listedOnMarketplace") : t("ownerHome.removedFromMarketplace"));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("ownerHome.couldNotUpdateListing"));
    }
  }

  if (!properties) return <div className="spinner" />;

  const total = received + pending;
  const pct = total > 0 ? Math.round((received / total) * 100) : 0;

  const pgProps = properties.filter((p) => p.category === "pg");
  const bedTotal = pgProps.reduce((n, p) => n + (p.occupancySummary?.totalBeds ?? 0), 0);
  const bedFilled = pgProps.reduce((n, p) => n + (p.occupancySummary?.occupiedBeds ?? 0), 0);
  const bedPct = bedTotal > 0 ? Math.round((bedFilled / bedTotal) * 100) : 0;

  const residentialProps = properties.filter((p) => p.category === "independent");
  const commercialProps = properties.filter((p) => p.category === "commercial");
  const shown =
    filter === "residential" ? residentialProps : filter === "commercial" ? commercialProps : filter === "pg" ? pgProps : properties;
  const emptyFilterKey =
    filter === "residential"
      ? "emptyNoIndependent"
      : filter === "commercial"
      ? "emptyNoCommercial"
      : filter === "pg"
      ? "emptyNoPg"
      : "emptyNoProperties";
  const sectionTitle =
    properties.length > 0 && properties.every((p) => p.category === "pg")
      ? t("ownerHome.sectionTitlePg")
      : t("ownerHome.sectionTitleProperties");

  return (
    <>
      <div className="summary-card ledger-card">
        <div className="ledger-head">
          <span className="ledger-title">{pending > 0 ? t("ownerHome.pendingThisMonth") : t("ownerHome.thisMonth")}</span>
          <span className="ledger-pill">
            <span className="dot" />
            {t("ownerHome.collectedPill", { pct })}
          </span>
        </div>
        <div className="ledger-money">
          <span className="ledger-big">{fmtINR(received)}</span>
          <span className="ledger-of">/ {fmtINR(total)}</span>
        </div>
        <div className="ledger-progress-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="ledger-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="ledger-note">
          <span><b>{fmtINR(received)}</b> {t("ownerHome.paidInFullCaption")}</span>
          <span><b>{fmtINR(pending)}</b> {t("ownerHome.dueSoonCaption")}</span>
        </div>
        {pendingEntryIds.length > 0 && (
          <button className="remind-all-btn" disabled={remindingAll} onClick={remindAll}>
            {remindingAll ? t("common.saving") : t("ownerHome.remindAll")}
          </button>
        )}
      </div>

      <ClaimsToConfirm />

      <HomePromoCarousel onNavigate={onNavigate} />

      {pgProps.length > 0 && (
        <div className="occ-summary-card occ-ring-row">
          <ProgressRing pct={bedPct} size={52} stroke={6} track="var(--brand-100)" color="var(--brand-cta)">
            <span className="occ-ring-pct">{bedPct}%</span>
          </ProgressRing>
          <div>
            <div className="occ-summary-label">{t("ownerHome.bedOccupancy")}</div>
            <div className="occ-summary-sub" style={{ marginTop: 2 }}>
              {t("ownerHome.bedsFilledLine", { filled: bedFilled, total: bedTotal })}
            </div>
          </div>
        </div>
      )}

      {properties.length > 0 && (
        <div className="stat-grid">
          <button type="button" className="stat-tile-e props" onClick={() => onNavigate("listings")}>
            <div className="stat-num-rect">{properties.length}</div>
            <div className="stat-label">{t("ownerHome.propertiesTileLabel")}</div>
          </button>
          <button
            type="button"
            className={`stat-tile-e tenants${showTenants ? " active" : ""}`}
            onClick={() => setShowTenants((v) => !v)}
          >
            <div className="stat-num-rect">{pendingTenants.length}</div>
            <div className="stat-label">{t("ownerHome.tenantsToFollowUp")}</div>
          </button>
        </div>
      )}

      {showTenants && (
        <div style={{ marginBottom: 14 }}>
          {pendingTenants.length === 0 ? (
            <div className="empty-state">{t("ownerHome.allCaughtUp")}</div>
          ) : (
            pendingTenants
              .slice(0, 3)
              .map((row) => (
                <TenantFollowUpRow
                  key={row.entry.id}
                  row={row}
                  onMarkPaid={markTenantPaid}
                  onRemind={remindTenant}
                  onOpen={() => onNavigate("ledgers")}
                />
              ))
          )}
        </div>
      )}

      <div className="section-head">
        <h2>{sectionTitle}</h2>
        <span className="count">{t("ownerHome.totalCount", { count: properties.length })}</span>
      </div>

      {properties.length > 0 && (
        <div className="chip-row-scroll">
          {(["all", "residential", "commercial", "pg"] as const).map((f) => (
            <div key={f} className={`chip${filter === f ? " selected" : ""}`} onClick={() => setFilter(f)}>
              {t(`ownerHome.filter${f === "all" ? "All" : f === "residential" ? "Residential" : f === "commercial" ? "Commercial" : "Pg"}`)}
            </div>
          ))}
        </div>
      )}

      <div className="prop-list">
        {properties.length === 0 && (
          <div className="empty-add-cta">
            <button onClick={() => onNavigate("listings", "__add__")} aria-label={t("ownerHome.addPropertyAria")}>+</button>
            <span>{t("ownerHome.emptyNoProperties")}</span>
          </div>
        )}
        {properties.length > 0 && shown.length === 0 && (
          <p className="empty-hint">{t(`ownerHome.${emptyFilterKey}`)}</p>
        )}
        {shown.map((p) =>
          p.category === "pg" ? (
            <PgCard key={p.id} p={p} onToggle={() => toggleToLet(p)} onOpen={() => onNavigate("listings")} />
          ) : p.units?.length ? (
            <MultiUnitCard key={p.id} p={p} onOpen={() => onNavigate("listings", p.id)} />
          ) : p.status === "occupied" ? (
            <OccupiedCard key={p.id} p={p} status={ledgerStatus[p.id]} onNavigate={onNavigate} onChanged={load} />
          ) : (
            <VacantCard
              key={p.id}
              p={p}
              onToggle={() => toggleToLet(p)}
              onOpen={() => onNavigate("listings")}
              onAddTenant={() => onNavigate("listings", p.id)}
              onChanged={load}
            />
          )
        )}
      </div>

      {properties.length > 0 && (
        <button className="fab-add" onClick={() => onNavigate("listings", "__add__")} aria-label={t("ownerHome.addPropertyAria")}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          {t("ownerHome.addUnitFab")}
        </button>
      )}
    </>
  );
}

function OccupiedCard({
  p,
  status,
  onNavigate,
  onChanged,
}: {
  p: Property;
  status?: "paid" | "pending";
  onNavigate: (v: "listings" | "ledgers" | "requests") => void;
  onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [managing, setManaging] = useState(false);
  const [editing, setEditing] = useState<"rent" | "maintenance" | "remove" | null>(null);
  const [rentInput, setRentInput] = useState(String(p.tenancy?.rent ?? ""));
  const [maintInput, setMaintInput] = useState(String(p.maintenance ?? ""));
  const [busy, setBusy] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const toast = useToast();
  const { user } = useAuth();
  const { t } = useLanguage();

  function toggleExpanded() {
    setExpanded((v) => {
      const next = !v;
      if (!next) {
        setManaging(false);
        setEditing(null);
      }
      return next;
    });
  }

  async function saveRent() {
    if (!p.tenancy) return;
    setBusy(true);
    try {
      await api.patch(`/api/tenancies/${p.tenancy.id}`, { rent: Number(rentInput) || 0 });
      toast(t("ownerHome.rentUpdated"));
      setEditing(null);
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("ownerHome.couldNotUpdateRent"));
    } finally {
      setBusy(false);
    }
  }

  async function saveMaintenance() {
    setBusy(true);
    try {
      await api.patch(`/api/properties/${p.id}`, { maintenance: Number(maintInput) || 0 });
      toast(t("ownerHome.maintenanceUpdated"));
      setEditing(null);
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("ownerHome.couldNotUpdateMaintenance"));
    } finally {
      setBusy(false);
    }
  }

  // Real <a target="_blank"> hrefs, not window.open() — a JS-invoked popup
  // gets silently blocked by the browser unless it's the direct synchronous
  // result of the click, which an onClick handler that reads state first
  // doesn't reliably count as. Same reason InstallAppPrompt's WhatsApp
  // button above uses a real anchor instead of an onClick handler.
  const whatsappReceiptHref = p.tenancy?.phone
    ? `https://wa.me/91${p.tenancy.phone}?text=${encodeURIComponent(
        t("ownerHome.whatsappReceiptMessage", {
          tenant: p.tenancy.name,
          property: p.label,
          amount: fmtINR(p.tenancy.rent),
          status: status === "paid" ? t("ownerHome.paid") : t("ownerHome.pending"),
          owner: user?.name ?? "",
        })
      )}`
    : null;

  // Same upi://pay deep-link format TenantPayments already builds for a
  // tenant paying an owner — here the owner forwards it to their tenant via
  // WhatsApp instead of tapping it themselves, since a upi:// link only
  // makes sense opened on the payer's own device.
  const upiPayLinkHref =
    user?.upiId && p.tenancy?.phone
      ? `https://wa.me/91${p.tenancy.phone}?text=${encodeURIComponent(
          t("ownerHome.upiPayLinkMessage", {
            tenant: p.tenancy.name,
            property: p.label,
            amount: fmtINR(p.tenancy.rent),
            owner: user.name ?? "",
            link: `upi://pay?${new URLSearchParams({
              pa: user.upiId,
              pn: user.name ?? "",
              am: String(p.tenancy.rent),
              cu: "INR",
              tn: `Rent - ${p.label}`,
            }).toString()}`,
          })
        )}`
      : null;

  if (showAgreement && p.tenancy) {
    return (
      <div className="doc-overlay">
        <RentalAgreement
          tenancyId={p.tenancy.id}
          tenantName={p.tenancy.name}
          tenantPhone={p.tenancy.phone}
          ownerName={user?.name}
          ownerPhone={user?.phone}
          propertyLabel={p.label}
          propertyLocality={p.locality}
          propertyType={p.type}
          rent={p.tenancy.rent}
          deposit={p.tenancy.deposit}
          startDate={p.tenancy.startDate}
          onBack={() => setShowAgreement(false)}
        />
      </div>
    );
  }

  return (
    <div className="prop-card occupied">
      <div className="prop-top" onClick={toggleExpanded} style={{ cursor: "pointer" }}>
        <div className="prop-top-main">
          <PropThumb p={p} />
          <div>
            <div className="prop-addr">{p.label}</div>
            <div className="prop-locality">{p.locality}</div>
            <CategoryChip p={p} />
            <span className="prop-chip">{p.type}</span>
          </div>
        </div>
        <span className={`badge ${status === "paid" ? "paid" : "pending"}`}>{status === "paid" ? t("ownerHome.paid") : t("ownerHome.pending")}</span>
      </div>
      <div className="prop-body">
      <div className="prop-mid" onClick={toggleExpanded} style={{ cursor: "pointer" }}>
        <div className="tenant-line">{t("ownerHome.tenantLabel")} &middot; <b>{p.tenancy?.name}</b></div>
        <div className="rent-amt">{fmtINR(p.tenancy?.rent ?? 0)}<span style={{ fontSize: 10.5, color: "var(--ink-faint)", fontWeight: 600 }}>{t("ownerHome.perMonth")}</span></div>
      </div>
      {p.category === "commercial" && (p.carpetAreaSqft || p.gstRegistrationPossible) && (
        <div className="commercial-line">
          <span>{p.carpetAreaSqft ? `${p.carpetAreaSqft.toLocaleString("en-IN")} ${t("ownerHome.sqftSuffix")}` : ""}</span>
          {p.gstRegistrationPossible && <span className="badge cash">{t("addPropertyForm.gstChip")}</span>}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        {whatsappReceiptHref ? (
          <a
            className="btn-outline btn-sm"
            style={{ flex: 1, textAlign: "center", textDecoration: "none" }}
            href={whatsappReceiptHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {t("ownerHome.sendWhatsappReceiptBtn")}
          </a>
        ) : (
          <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={(e) => { e.stopPropagation(); toast(t("ownerHome.noTenantPhone")); }}>
            {t("ownerHome.sendWhatsappReceiptBtn")}
          </button>
        )}
        {upiPayLinkHref ? (
          <a
            className="btn-outline btn-sm"
            style={{ flex: 1, textAlign: "center", textDecoration: "none" }}
            href={upiPayLinkHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {t("ownerHome.directUpiPayLinkBtn")}
          </a>
        ) : (
          <button
            className="btn-outline btn-sm"
            style={{ flex: 1 }}
            onClick={(e) => { e.stopPropagation(); toast(t(user?.upiId ? "ownerHome.noTenantPhone" : "ownerHome.noUpiIdSet")); }}
          >
            {t("ownerHome.directUpiPayLinkBtn")}
          </button>
        )}
      </div>

      <button
        className="btn-outline btn-sm"
        style={{ marginTop: 8, width: "100%" }}
        onClick={(e) => {
          e.stopPropagation();
          setShowAgreement(true);
        }}
      >
        {t("ownerListings.rentalAgreementButton")} <span className="mi-right">›</span>
      </button>

      {expanded && (
        <>
          <div className="card" style={{ background: "var(--surface-alt)", marginTop: 12 }}>
            <div className="card-row">
              <span className="sub">{t("ownerHome.phoneLabel")}</span>
              <b>{p.tenancy?.phone || t("ownerHome.noPhoneOnFile")}</b>
            </div>
            <div className="card-row" style={{ marginTop: 8 }}>
              <span className="sub">{t("ownerHome.aadhaarLabel")}</span>
              <b>{p.tenancy?.aadhaar || "—"}</b>
            </div>
            <div className="card-row" style={{ marginTop: 8 }}>
              <span className="sub">{t("ownerHome.depositLabel")}</span>
              <b>{fmtINR(p.tenancy?.deposit ?? 0)}</b>
            </div>
            <div className="card-row" style={{ marginTop: 8 }}>
              <span className="sub">{t("ownerHome.moveInDateLabel")}</span>
              <b>
                {p.tenancy?.startDate
                  ? new Date(p.tenancy.startDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                  : "—"}
              </b>
            </div>
          </div>

          <button className="btn-outline btn-sm" style={{ marginTop: 10 }} onClick={() => setManaging((v) => !v)}>
            {managing ? t("ownerHome.hideManageOptions") : t("ownerHome.manage")}
          </button>
        </>
      )}

      {managing && (
        <div className="menu-group">
          <div className="menu-item" onClick={() => onNavigate("ledgers")}>
            {t("ownerHome.rentalReceipts")} <span className="mi-right">›</span>
          </div>
          <div className="menu-item" onClick={() => setEditing(editing === "rent" ? null : "rent")}>
            {t("ownerHome.modifyRent")} <span className="mi-right">›</span>
          </div>
          <div className="menu-item" onClick={() => setEditing(editing === "maintenance" ? null : "maintenance")}>
            {t("ownerHome.maintenanceMenu")} <span className="mi-right">›</span>
          </div>
          <div className="menu-item danger" onClick={() => setEditing(editing === "remove" ? null : "remove")}>
            {t("ownerHome.removeTenant")}
          </div>
        </div>
      )}

      {editing === "rent" && (
        <div className="field" style={{ marginTop: 12, marginBottom: 0 }}>
          <label>{t("ownerHome.newRentLabel")}</label>
          <div className="input-wrap"><input type="number" value={rentInput} onChange={(e) => setRentInput(e.target.value)} /></div>
          <button className="btn-solid btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={saveRent}>
            {busy ? t("common.saving") : t("ownerHome.save")}
          </button>
        </div>
      )}
      {editing === "maintenance" && (
        <div className="field" style={{ marginTop: 12, marginBottom: 0 }}>
          <label>{t("ownerHome.maintenancePerMonthLabel")}</label>
          <div className="input-wrap"><input type="number" value={maintInput} onChange={(e) => setMaintInput(e.target.value)} /></div>
          <button className="btn-solid btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={saveMaintenance}>
            {busy ? t("common.saving") : t("ownerHome.save")}
          </button>
        </div>
      )}
      {editing === "remove" && p.tenancy && (
        <EndTenancyForm
          tenancy={p.tenancy}
          onCancel={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            setManaging(false);
            setExpanded(false);
            onChanged();
          }}
        />
      )}
      </div>
    </div>
  );
}

function VacantCard({
  p,
  onToggle,
  onOpen,
  onAddTenant,
  onChanged,
}: {
  p: Property;
  onToggle: () => void;
  onOpen: () => void;
  onAddTenant: () => void;
  onChanged: () => void;
}) {
  const { t } = useLanguage();
  return (
    <div className="prop-card vacant" onClick={onOpen} style={{ cursor: "pointer" }}>
      <div className="prop-top">
        <div className="prop-top-main">
          <PropThumb p={p} />
          <div>
            <div className="prop-addr">{p.label}</div>
            <div className="prop-locality">{p.locality}</div>
            <CategoryChip p={p} />
            <span className="prop-chip">{p.type}</span>
          </div>
        </div>
        <span className="badge vacant">{t("ownerHome.vacantBadge")}</span>
      </div>
      <div className="prop-body">
      <div className="expected-rent">
        {t("ownerHome.expectedRentLabel")} &middot; <b>{fmtINR(p.expectedRent ?? 0)}{t("ownerHome.perMonth")}</b>
        {p.expectedDeposit ? t("ownerHome.depositSuffix", { amount: fmtINR(p.expectedDeposit) }) : ""}
      </div>
      {p.category === "commercial" && (p.carpetAreaSqft || p.gstRegistrationPossible) && (
        <div className="commercial-line">
          <span>{p.carpetAreaSqft ? `${p.carpetAreaSqft.toLocaleString("en-IN")} ${t("ownerHome.sqftSuffix")}` : ""}</span>
          {p.gstRegistrationPossible && <span className="badge cash">{t("addPropertyForm.gstChip")}</span>}
        </div>
      )}
      {p.maintenance ? <div className="maintenance-line">{t("ownerHome.maintenanceLine", { amount: fmtINR(p.maintenance) })}</div> : null}
      <div className="vacant-row">
        <div className="vacant-info">
          <div className="vacant-label">{t("ownerHome.listOnToLet")}</div>
          <div className="vacant-sub">
            {p.toLet ? (<><span className="live-dot" /> {t("ownerHome.liveVisible")}</>) : t("ownerHome.notListedYet")}
          </div>
        </div>
        <button
          className={`toggle${p.toLet ? " on" : ""}`}
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          aria-label={t("ownerHome.toggleToLetAria")}
        >
          <i />
        </button>
      </div>
      <BoostListing property={p} onChanged={onChanged} />
      <button className="btn-outline" onClick={(e) => { e.stopPropagation(); onAddTenant(); }}>
        {t("ownerHome.addTenantButton")}
      </button>
      </div>
    </div>
  );
}

function MultiUnitCard({ p, onOpen }: { p: Property; onOpen: () => void }) {
  const { t } = useLanguage();
  const { totalBeds = 0, vacantBeds = 0 } = p.occupancySummary ?? {};
  const occupiedBeds = totalBeds - vacantBeds;
  const pct = totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0;
  const hasVacancy = vacantBeds > 0;
  const houseCount = p.units?.length ?? 0;
  const housesLiveCount = p.units?.filter((u) => u.toLet).length ?? 0;
  const anyHouseLive = housesLiveCount > 0;

  return (
    <div className="prop-card" onClick={onOpen} style={{ cursor: "pointer" }}>
      <div className="prop-top">
        <div className="prop-top-main">
          <PropThumb p={p} />
          <div>
            <div className="prop-addr">{p.label}</div>
            <div className="prop-locality">{p.locality}</div>
            <CategoryChip p={p} />
          </div>
        </div>
        <span className="badge vacant">
          {t("ownerListings.vacantBedsFraction", { vacant: vacantBeds, total: totalBeds })}
        </span>
      </div>
      <div className="prop-body">
      <div className="bed-fill-bar" style={{ marginTop: 14, marginBottom: 18 }}><i style={{ width: `${pct}%` }} /></div>
      <div className="vacant-row" style={{ marginTop: 0, paddingBottom: hasVacancy ? 16 : 0 }}>
        <div className="vacant-info">
          <div className="vacant-label">{t("ownerHome.manageHouses")}</div>
          <div className="vacant-sub">{t("ownerListings.occupiedFraction", { occupied: occupiedBeds, total: totalBeds })}</div>
        </div>
        <span style={{ color: "var(--brand-700)", fontWeight: 800, fontSize: 18, flexShrink: 0 }}>›</span>
      </div>
      {hasVacancy && (
        <div className="vacant-row" style={{ marginTop: 0 }}>
          <div className="vacant-info">
            <div className="vacant-label">{t("ownerHome.toLetStatus")}</div>
            <div className="vacant-sub">
              {anyHouseLive ? (
                <><span className="live-dot" /> {t("ownerHome.housesLiveLine", { count: housesLiveCount, total: houseCount })}</>
              ) : (
                t("ownerHome.notListedYet")
              )}
            </div>
          </div>
          <button
            className={`toggle${anyHouseLive ? " on" : ""}`}
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            aria-label={t("ownerHome.manageToLetAria")}
          >
            <i />
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

function PgCard({ p, onToggle, onOpen }: { p: Property; onToggle: () => void; onOpen: () => void }) {
  const { t } = useLanguage();
  const OCC_LABELS: Record<string, string> = {
    boys: t("ownerHome.occBoys"),
    girls: t("ownerHome.occGirls"),
    "co-ed": t("ownerHome.occCoEd"),
  };
  const { totalBeds = 0, vacantBeds = 0, occupiedBeds = 0 } = p.occupancySummary ?? {};
  const pct = totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0;
  const hasVacancy = vacantBeds > 0;
  const chips = p.facilities.slice(0, 3);
  const moreCount = p.facilities.length - chips.length;

  return (
    <div className="prop-card pg" onClick={onOpen} style={{ cursor: "pointer" }}>
      <div className="prop-top">
        <div className="prop-top-main">
          <PropThumb p={p} />
          <div>
            <div className="prop-addr">{p.label}</div>
            <div className="prop-locality">{p.locality}</div>
            <CategoryChip p={p} />
            <span className="prop-chip">{OCC_LABELS[p.occupancyType ?? ""] ?? t("ownerHome.occDefault")}</span>
          </div>
        </div>
        <span className={`badge ${hasVacancy ? "pending" : "paid"}`}>{t("ownerHome.bedsFraction", { filled: occupiedBeds, total: totalBeds })}</span>
      </div>
      <div className="prop-body">
      <div className="bed-fill-bar"><i style={{ width: `${pct}%` }} /></div>
      {p.facilities.length > 0 && (
        <div className="pg-chips" style={{ marginTop: 11 }}>
          {chips.map((f) => (
            <span className="prop-chip facility" key={f}>{f}</span>
          ))}
          {moreCount > 0 && <span className="prop-chip facility">+{moreCount}</span>}
        </div>
      )}
      {hasVacancy ? (
        <div className="vacant-row">
          <div className="vacant-info">
            <div className="vacant-label">{t("ownerHome.listVacantBedsToLet")}</div>
            <div className="vacant-sub">
              {p.toLet ? (<><span className="live-dot" /> {t("ownerHome.liveVisible")}</>) : t("ownerHome.notListedYet")}
            </div>
          </div>
          <button
            className={`toggle${p.toLet ? " on" : ""}`}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            aria-label={t("ownerHome.toggleToLetAria")}
          >
            <i />
          </button>
        </div>
      ) : (
        <div className="maintenance-line" style={{ marginTop: 12 }}>{t("ownerHome.fullyOccupied")}</div>
      )}
      </div>
    </div>
  );
}
